import { logger } from "@/util/logger";
import { globalAxiosInstance } from "./axios";
import type {
  ConnectPipecatDemoSessionRequest,
  CreatePipecatDemoSessionRequest,
  PipecatConnectResponse,
  PipecatStartResponse,
} from "@/types/roleplay-session";

const isLocal = process.env.NEXT_PUBLIC_ENV === "local";

export interface CoachingAvailabilityRequest {
  type: "preRoleplay" | "postRoleplay";
  roleplayId: string;
  roleplayType: string;
  demoLinkId?: string;
  demoSessionId?: string;
  demoResultId?: string;
}

export const getCoachingAvailabilityApi = async (
  body: CoachingAvailabilityRequest
) => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  return globalAxiosInstance.post<{ data: { limitExceeded: boolean } }>(
    "/usage/coaching-status",
    body,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
};

/**
 * Optional bearer header for routes that are not auth-guarded but still want to
 * know the caller's org, so uploads land under `<orgId>/<feature>/…`.
 *
 * Deliberately not the protected interceptor: that one redirects to /signin when
 * no token exists, which would break the demo roleplay and interview flows that
 * run logged-out. Here a missing token simply means no header.
 */
/**
 * Org id from localStorage, for upload routes that build `<orgId>/<feature>/…`
 * keys but are not auth-guarded (demo roleplays and candidate interviews run
 * logged-out, so the server has no verified org to work from).
 *
 * The server treats this as the lowest-precedence source — a verified token
 * always wins — and validates the shape before it reaches a key.
 */
const orgIdFromStorage = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = localStorage.getItem("org");
    if (!raw) return undefined;
    const id = JSON.parse(raw)?._id;
    return typeof id === "string" && /^[a-f0-9]{24}$/i.test(id)
      ? id
      : undefined;
  } catch {
    return undefined;
  }
};

/** Merge the caller's body with the org id, when we know it. */
const withOrgId = <T extends object>(body: T): T & { orgId?: string } => {
  const orgId = orgIdFromStorage();
  return orgId ? { ...body, orgId } : body;
};

const optionalAuthHeader = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

/**
 * Storage backend for uploads.
 *
 * The `/gcs/*` routes mirror `/s3/*` one-for-one (same request and response
 * shapes), so switching providers is a path change. Set
 * NEXT_PUBLIC_STORAGE_PROVIDER to "gcs" to route through Google Cloud Storage;
 * anything else keeps S3.
 *
 * Only affects where NEW requests go — objects already on S3 keep resolving
 * through the S3 route.
 */
export const USE_GCS = process.env.NEXT_PUBLIC_STORAGE_PROVIDER === "gcs";

export const loginApi = async (body: {
  identifier: string;
  password: string;
  phoneNumber?: string | null;
  phoneCodeCountry?: string | null;
  userAgent?: any;
  consentText?: string;
}) => {
  const response = await globalAxiosInstance.post("/users/login", body);
  return response;
};

export const getShareSessionInfo = (token: string) => {
  return globalAxiosInstance.get("/shortlinks/share-session/info", {
    params: { token },
  });
};

export const sendShareOtp = (body: {
  token: string;
  channel: "email" | "phone";
}) => {
  return globalAxiosInstance.post("/shortlinks/share-session/send-otp", body);
};

export const verifyShareOtp = (body: { token: string; otp: string }) => {
  return globalAxiosInstance.post("/shortlinks/share-session/verify-otp", body);
};

export const verifyOtp = (data: {
  identifier: string;
  email?: string;
  phoneNumber?: string;
  otp: string;
}) => {
  return globalAxiosInstance.post("/users/verify-mfa-otp/", data);
};

export const getAllSchoolsApi = async () => {
  return await globalAxiosInstance.get(`/school/all-schools`);
};

export const registerApi = async (body: any) => {
  const response = await globalAxiosInstance.post(`/users/sign-up`, body);
  return response;
};

export const verifyOtpApi = async (body: any) => {
  const response = await globalAxiosInstance.post(`/users/verify-otp`, body);
  return response;
};

export const resendOtpApi = async (body: any) => {
  const response = await globalAxiosInstance.post(`/auth/resend-otp`, body);
  return response;
};

export const setPassApi = async (body: any) => {
  const response = await globalAxiosInstance.post(`/users/set-password`, body);
  return response;
};

export const sendEmailApi = async (body: any) => {
  const response = await globalAxiosInstance.post(
    `/users/forgot-password`,
    body
  );
  return response;
};

export const sendFeedback = async (body: any) => {
  const response = await globalAxiosInstance.post(`/school/contact-us`, body);
  return response;
};

export const getInterview = async (interiewId: string) => {
  return globalAxiosInstance.get(`/user-interview/${interiewId}`);
};

export const getShortLink = async (linkId: string, token?: string) => {
  const params = {
    token,
  };

  return globalAxiosInstance.get(`/shortlinks/${linkId}`, { params });
};

export const getInterviewRoleplay = async (
  roleplayId: string,
  interviewId: string
) => {
  const url = `/roleplay/by-chapter-interview/`;
  return globalAxiosInstance.get(url, {
    params: { roleplayId, interviewId },
  });
};

export const createDemoRoleplay = async (body: any, analysisId?: string) => {
  if (body?.phoneNumber === null) delete body.phoneNumber;

  return globalAxiosInstance.post(
    `/shortlinks/create-demo-roleplay${analysisId ? `?analysisId=${analysisId}` : ""}`,
    body
  );
};

export async function savePipecatMediaApi(
  resultId: string,
  body: { token: string; mediaUrl: string }
): Promise<{ stored: boolean }> {
  const { data } = await globalAxiosInstance.post<{ stored: boolean }>(
    `/roleplay/v2/sessions/${resultId}/media`,
    body
  );
  return data;
}

export async function connectPipecatDemoSessionApi(
  body: ConnectPipecatDemoSessionRequest
): Promise<PipecatConnectResponse> {
  const { data } = await globalAxiosInstance.post<PipecatConnectResponse>(
    "/shortlinks/v2/demo-sessions/connect",
    body
  );
  return data;
}

export async function createPipecatDemoSessionApi(
  body: CreatePipecatDemoSessionRequest
): Promise<PipecatStartResponse> {
  const { data } = await globalAxiosInstance.post<PipecatStartResponse>(
    "/shortlinks/v2/demo-sessions",
    body
  );
  return data;
}

export const renewDemoSessionUsage = (resultId: string) => {
  return globalAxiosInstance.post(`/roleplay/demo/${resultId}/usage`);
};

export const verifyInterview = async (body: {
  email: string;
  interviewId: string;
}) => {
  return globalAxiosInstance.post(`/user-interview/verify-interview`, body);
};

export const startInterview = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/start-interview`, body);
};

export const startLatestInterview = async (body: any) => {
  return globalAxiosInstance.patch(
    `/user-interview/start-latest-interview`,
    body
  );
};

export const startMcqInterview = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/start-mcq-interview`, body);
};

export const saveInterviewResult = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/save-answer`, body);
};

export const saveMainInterviewResult = async (body: any) => {
  return globalAxiosInstance.patch(
    `/user-interview/save-interview-answer`,
    body
  );
};

export const saveMcqInterviewResult = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/save-mcq-answer`, body);
};

export const skipInterviewQuestion = async (body: {
  _id: string;
  answerUUID: string;
}) => {
  return globalAxiosInstance.patch(`/user-interview/skip-question`, body);
};

export const exitInterviewFullscreen = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/exit-fullscreen`, body);
};

export const endInterview = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/end-interview`, body);
};

export const endMainInterview = async (body: any) => {
  return globalAxiosInstance.patch(`/user-interview/end-main-interview`, body);
};

export const getInterviewVideoPresigned = async (body: {
  contentType: string;
  type: string;
  provider?: "google" | "aws";
  interviewId?: string;
  evaluationId?: string;
}) => {
  return globalAxiosInstance.post(
    USE_GCS
      ? `/gcs/media/interview/presigned-url`
      : `/user-interview/get-presigned-url`,
    withOrgId(body),
    { headers: optionalAuthHeader() }
  );
};

export const uploadVideo = async (body: any) => {
  return globalAxiosInstance.post(`user-interview/upload-video`, body, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getInterviewFeedback = async (interviewId: string) => {
  return globalAxiosInstance.get(`/user-interview/feedback/${interviewId}`);
};
export const submitInterviewFeedback = async (body: any) => {
  return globalAxiosInstance.post(`/user-interview/feedback/`, body);
};

export const getJobList = async () => {
  return globalAxiosInstance.get(`/jobs`);
};

export const getJob = async (jobId: string) => {
  return globalAxiosInstance.get(`/jobs/${jobId}`);
};

export const getOrg = async (orgId: string) => {
  return globalAxiosInstance.get(`/organizations/${orgId}`);
};

// Frontend
export const getOneOrg = async (orgId: string, linkId: string) => {
  return globalAxiosInstance.post(`/organizations/get-org/${orgId}`, {
    linkId,
  });
};

export const getResumePresigned = async (body: { contentType: string }) => {
  return globalAxiosInstance.post(
    USE_GCS
      ? `/gcs/media/job-application/presigned-url`
      : `/job-applications/get-presigned-url`,
    withOrgId(body),
    { headers: optionalAuthHeader() }
  );
};

// Reads always use the S3 route: it resolves GCS urls, S3 urls and bare keys
// (probing GCS before S3), whereas the GCS route returns S3 urls unsigned,
// which breaks every asset uploaded before the migration.
export const getPresignedUrl = async (url: string) => {
  return globalAxiosInstance.get(`s3/presigned-url`, {
    params: { url },
  });
};

export const sendResume = async (body: any) => {
  return globalAxiosInstance.post(
    `https://resume-parser-mtepodzz4a-as.a.run.app`,
    body
  );
};

export const applyJob = async (body: any) => {
  // -("body", body);

  const response = await globalAxiosInstance.post(`/job-applications`, body);
  return response;
};

export const uploadResume = (body: any) => {
  if (isLocal) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            resumeUrl:
              "https://storage.googleapis.com/mple-app-test/user-resumes/88005ba1-b4f2-4673-acf3-68ec866d1b27.pdf",
          },
        });
      }, 800);
    });
  }
  // Resumes are stored under `<orgId>/user-resume/…`, but this route is public
  // (candidates apply logged out), so the server has no verified org to work
  // from. Attach ours when we know it; the server validates the shape and
  // simply drops the prefix when it's absent.
  const orgId = orgIdFromStorage();
  if (orgId && body instanceof FormData && !body.has("orgId")) {
    body.append("orgId", orgId);
  }

  return globalAxiosInstance.post(`/user-interview/upload-resume`, body, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};
export const evaluateResume = (body: any) => {
  if (isLocal) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            name: "Zaid Khan",
            email: "mrzaidkhan007@gmail.com",
            contactNo: "+91 9916655554",
            dob: "",
            education: [
              {
                degree: "MBA",
                specialization: "International Business",
                college: "ESC Pau Business School",
                university: "France",
                yearOfPassing: "2018",
              },
              {
                degree: "Bachelors in Business Management",
                specialization: "Finance",
                college: "Center for Management Studies",
                university: "Bangalore, India",
                yearOfPassing: "2016",
              },
              {
                degree: "High School",
                specialization: "Commerce",
                college: "SBMJC, Jain College",
                university: "State Board, Bangalore, India",
                yearOfPassing: "2013",
              },
            ],
            workExperience: [
              {
                company: "Zsquare Realestate",
                designation: "Senior Property Consultant Team Lead",
                start: "Oct 2024",
                end: "Present",
                isCurrentlyWorking: true,
              },
              {
                company: "Investoxpert Pvt Advisors Ltd",
                designation: "Senior Relationship Manager",
                start: "June 2024",
                end: "Oct 2025",
                isCurrentlyWorking: false,
              },
              {
                company: "DNK Real Estate",
                designation: "Property Manager",
                start: "Dec 2021",
                end: "May 2024",
                isCurrentlyWorking: false,
              },
            ],
            profileTags: [
              "Senior Property Consultant",
              "Senior Relationship Manager",
              "Property Manager",
            ],
            skills: [
              "Listening",
              "Negotiation",
              "Clear Communication",
              "Market expertise",
              "Persuasion",
              "Closing Deals",
              "Customer Service",
            ],
            similarity: {
              similarityScore: 2,
              explanation:
                "The resume is not a good fit for the job description as it focuses on real estate roles, while the job description is not provided.",
            },
            totalWorkExperience: 3,
            gap: [],
            linkedIn_profile_url: "/in/mrzaid-khan",
            resumeUrl:
              "https://storage.googleapis.com/mple-app-test/user-resumes/af9d0004-6631-4357-9dfd-a1ddffcadbd6.pdf",
          },
        });
      }, 2000); // Delay for 1 minute (60000 milliseconds)
    });
  }
  return globalAxiosInstance.post(`/user-interview/evaluate-resume`, body);
};

export const assignInterviewToUser = (body: any) => {
  return globalAxiosInstance.post(`/user-interview/assign`, body);
};

export const sendInvitationEmail = (body: { assignedInterviewId: string }) => {
  if (isLocal) {
    return Promise.resolve({ data: { success: true } });
  }
  return globalAxiosInstance.post(`/email/interview-invite/`, body);
};

export const getDemoRoleplayById = (roleplayId: string) => {
  return globalAxiosInstance.get(`/roleplay/demo/${roleplayId}`);
};

export const getDemoRoleplayResultById = (roleplayId: string) => {
  return globalAxiosInstance.get(`/roleplay/demo-result/${roleplayId}`);
};

export const saveDemoRoleplayResult = (body: any) => {
  return globalAxiosInstance.post(`/roleplay/demo/save-result`, body);
};

export const saveDemoPptV3QuestionResponse = (resultId: string, body: any) => {
  return globalAxiosInstance.post(
    `/roleplay/demo/ppt-v3/question-response/${resultId}`,
    body
  );
};

export const initiateS3Stream = (body: {
  type: string;
  contentType: string;
  resultId?: string;
  resultType?: string;
}) => {
  return globalAxiosInstance.post(
    USE_GCS ? `/gcs/media/roleplay/initiate` : `/roleplay/initiate/`,
    withOrgId(body),
    { headers: optionalAuthHeader() }
  );
};

export const uploadS3StreamChunk = (formData: FormData) => {
  return globalAxiosInstance.post(
    USE_GCS ? `/gcs/media/roleplay/chunk` : `/roleplay/chunk/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const completeS3Stream = (body: {
  uploadId: string;
  key: string;
  parts: any[];
}) => {
  return globalAxiosInstance.post(
    USE_GCS ? `/gcs/media/roleplay/complete` : `/roleplay/complete/`,
    body
  );
};

export const abortS3Stream = (body: { uploadId: string; key: string }) => {
  return globalAxiosInstance.post(
    USE_GCS ? `/gcs/media/roleplay/abort` : `/roleplay/abort/`,
    body
  );
};

export const translateText = async (body: {
  text: string;
  language: string;
  orgId?: string;
  userId?: string;
}) => {
  return globalAxiosInstance.post("/ai-examiner/translate", {
    ...body,
    text: encodeURIComponent(body.text),
  });
};

export const translateArrayOfText = async (body: {
  textArray: string[];
  language: string;
  orgId?: string;
  userId?: string;
}) => {
  return globalAxiosInstance.post("/ai-examiner/translate_array", body);
};

export const transformedBulletText = async (body: {
  text: string;
  orgId?: string;
  userId?: string;
}) => {
  return globalAxiosInstance.post("/ai-examiner/make-bullet", body);
};

export const submitRoleplayRating = async (
  documentId: any,
  feedbackRating: any,
  feedback: any
) => {
  const body = {
    documentId,
    feedbackRating,
    feedback,
  };
  return globalAxiosInstance.post("/roleplay/update-rating/", body);
};

export const submitDemoRoleplayRating = async (
  roleplayId: string,
  feedbackRating: any,
  email: string,
  feedback: any
) => {
  return globalAxiosInstance.post("/roleplay/demo-rating/", {
    roleplayId,
    feedbackRating,
    email,
    feedback,
  });
};

export const getOpenBookDataApi = async (id: string) => {
  return await globalAxiosInstance.get(`/users/open-book-details/${id}`);
};

export const translateIntro = async (body: {
  gender: string;
  lang: string;
  accent: string;
  voices: any;
  id?: string;
  text?: string;
  orgId?: string;
  blendShapes?: boolean;
  noAudio?: boolean;
  usePCM?: boolean;
  voiceId?: string;
  analysisId?: string;
  useChunking?: boolean;
}) => {
  return globalAxiosInstance.post("bot/translate_intro", body);
};

export const magicLogin = (body: { token: string; userAgent?: any }) => {
  return globalAxiosInstance.post(`/users/magic-login`, body);
};

export const sendMagicLink = (body: {
  identifier: string;
  phoneNumber?: string | null;
  phoneCodeCountry?: string | null;
}) => {
  return globalAxiosInstance.post("/users/magic-link", body);
};

export const checkEmailApi = (body: {
  identifier: string;
  phoneCodeCountry?: string | null;
  phoneNumber?: string | null;
}) => {
  return globalAxiosInstance.post("/users/check-email-org", body);
};

export const getVapidPublicKey = async (): Promise<string> => {
  const response = await globalAxiosInstance.get(
    "/notifications/vapid-public-key"
  );
  return response.data.publicKey;
};

export const subscribeToNotifications = async (
  subscription: PushSubscription
): Promise<void> => {
  await globalAxiosInstance.post("/notifications/subscribe", subscription);
};

export const sendTestNotification = async (userId: string): Promise<void> => {
  await globalAxiosInstance.post("/notifications/send", { userId });
};
export const startFlask = async (bot_id: string): Promise<void> => {
  await globalAxiosInstance.get("ai-examiner/start-flask", {
    params: { bot_id },
  });
};

export const fetchModelGlb = (modelName: string) => {
  return globalAxiosInstance.get(`/users/get-model/${modelName}`);
};

export const postFeedback = (body: any) => {
  return globalAxiosInstance.post(`/feedback/`, body);
};

export const addErrorLog = (body: {
  userId?: string;
  error: any;
  errorDesc: any;
}) => {
  if (!body.userId) {
    delete body.userId;
  }

  return globalAxiosInstance.post(`/error-logs/`, {
    ...body,
    origin: window.location.href,
  });
};

export const fetchHeygenToken = (params?: {
  avatarId?: string;
  voiceId?: string;
  language?: string;
}) => {
  return globalAxiosInstance.get("ai-examiner/heygen_token", { params });
};

/**
 * Links the running avatar session to the result document.
 *
 * The vendor session is created before the result exists, so this is sent once
 * both ids are known. It is what lets the backend bill an abandoned session:
 * when the visitor closes the tab or the widget mid-call, the result save never
 * runs and this link is the only thing tying those credits to the session.
 */
export const attachHeygenSession = (body: {
  sessionId: string;
  resultId: string;
  resultType: "result" | "demo" | "interview";
}) => {
  return globalAxiosInstance.post("/roleplay/heygen-session/attach", body);
};

export const checkIfTrained = (botId: string) => {
  return globalAxiosInstance.get(`bot/is-trained/${botId}`);
};

export const saveAudioCache = (body: any) => {
  return globalAxiosInstance.post(`/audio-cache/`, withOrgId(body));
};

export const getAudioCache = (filter: any) => {
  return globalAxiosInstance.get(`/audio-cache/`, { params: filter });
};

export const findDataFields = (body: any) => {
  return globalAxiosInstance.post("/campaign/find-data-fields", body);
};

export const saveDataFields = (body: any) => {
  return globalAxiosInstance.post("/campaign/save-data-fields", body);
};

// APIS for INTERVIEW
export const getChapterAttemptDetailsApiInterview = async (data: {
  interviewId: any;
  bookId: string;
  chapterId: string;
}) => {
  logger.log(
    "🚀 ~ getChapterAttemptDetailsApiInterview ~ interviewId:",
    data.interviewId
  );
  logger.log(
    "🚀 ~ getChapterAttemptDetailsApiInterview ~ bookId:",
    data.bookId
  );
  logger.log(
    "🚀 ~ getChapterAttemptDetailsApiInterview ~ chapterId:",
    data.chapterId
  );
  return await globalAxiosInstance.post(
    `/users/chapter-attempt-details-interview`,
    data
  );
};

// ─── Interview Assessment APIs ────────────────────────────────────────────────

export const getAssessmentLangInterview = async (chapterId: string) => {
  return await globalAxiosInstance.get(
    `/chapters/interview-assessment-lang/${chapterId}`
  );
};

export const getAssessmentDataApiInterview = async (
  bookId: string,
  chapterId: string,
  language?: string
) => {
  logger.log("Reached Here");
  const response = await globalAxiosInstance.post(
    `/chapters/interview-assessment`,
    { bookId, chapterId, language }
  );
  return response;
};

type answers = {
  questionNo: number;
  answer: string[];
};

export const submitAssessmentDataApiInterview = async (
  bookId: string,
  chapterId: string,
  answers: answers[],
  interviewId: string,
  exitedFullScreen?: boolean,
  // multipleScreensDetected?: boolean,
  language?: string,
  resultVideo?: string,
  jobId?: string,
  assessmentResultId?: string
) => {
  const response = await globalAxiosInstance.post(
    `/chapters/submit-assessment-interview`,
    {
      bookId,
      chapterId,
      answers,
      exitedFullScreen,
      // multipleScreensDetected,
      interviewId,
      language,
      resultVideo,
      jobId,
      assessmentResultId,
    }
  );
  return response;
};

export const updateInterviewAssessmentStatus = async (
  interviewId: string,
  chapterId: string,
  status: "inprogress" | "completed",
  bookId?: string,
  jobId?: string,
  resultId?: string
) => {
  return await globalAxiosInstance.post(
    `/chapters/update-interview-assessment-status`,
    { interviewId, chapterId, status, bookId, jobId, resultId }
  );
};

export const initiateAssessmentVideoUploadInterview = async () => {
  return await globalAxiosInstance.post(
    `/chapters/interview-assessment-video/initiate`
  );
};

export const uploadAssessmentVideoPartInterview = async (
  formData: FormData
) => {
  return await globalAxiosInstance.post(
    `/chapters/interview-assessment-video/part`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const completeAssessmentVideoUploadInterview = async (data: {
  uploadId: string;
  key: string;
  parts: { ETag: string; PartNumber: number }[];
}) => {
  return await globalAxiosInstance.post(
    `/chapters/interview-assessment-video/complete`,
    data
  );
};

export const abortAssessmentVideoUploadInterview = async (data: {
  uploadId: string;
  key: string;
}) => {
  return await globalAxiosInstance.post(
    `/chapters/interview-assessment-video/abort`,
    data
  );
};

export const getInterviewBookApi = (jobId: string) => {
  logger.log("🚀 ~ getInterviewBookApi ~ jobId:", jobId);
  return globalAxiosInstance.get(`/users/interview-modules/${jobId}`);
};

export const getInterviewModulesApi = (
  bookId: string,
  jobId: string,
  interviewId: string
) => {
  return globalAxiosInstance.get(
    `/users/get-interview-modules/${bookId}/${jobId}/${interviewId}`
  );
};

export const getAssessmentHistoryResultApiInterview = async (
  testId: string
) => {
  const response = await globalAxiosInstance.get(
    `/assesment-results/fetch-interview-result/`,
    {
      params: { interviewId: testId },
    }
  );
  return response;
};

export const getSlideIntro = (body: any) => {
  return globalAxiosInstance.post("/bot/getSlideIntro", body);
};

export const getInterviewEvaluationChapter = async (chapterId: string) => {
  return await globalAxiosInstance.get(
    `/chapters/interview-evaluation/${chapterId}`
  );
};

// Interview Roleplay APIs
export const createInterviewRoleplay = async (body: {
  interviewId: string;
  roleplayData: any;
  roleplayId: string;
  jobId?: string;
  callType: string;
  userAgent: any;
  selectedLanguage: string;
  scripts?: any[];
  formData?: any;
  startedAt?: string;
  isPaidSTT?: boolean;
  usedHint?: boolean;
  deviceLogs?: string[];
  maxTokens?: number;
  realAvatars?: boolean;
  bookId?: string;
  usePPT?: boolean;
  startSlide?: number;
  resumeData?: any;
}) => {
  return globalAxiosInstance.post("/roleplay/interview/start", body);
};

export const saveInterviewRoleplayResult = async (body: {
  _id: string;
  scripts?: any[];
  formData?: any;
  status?: string;
  endedAt?: string;
  usedHint?: boolean;
  resultVideo?: string;
  resultMedias?: string[];
  recordingStartedAt?: number | null;
}) => {
  return globalAxiosInstance.put(
    `/roleplay/interview/save-result/${body._id}`,
    body
  );
};

export const getRoleplayInterviewResult = async (resultId: string) => {
  return globalAxiosInstance.get(`/roleplay/interview/result/${resultId}`);
};

export const getEvaluationInterviewResults = (
  resultId: string,
  startFromModule: boolean
) => {
  return globalAxiosInstance.get(
    `/user-interview/evaluation-interview-results/${resultId}`,
    {
      params: {
        startFromModule,
      },
    }
  );
};

export const fetchRoleplayResult = async (roleplayId: string) => {
  logger.log(
    "I have Reached in frontend API ->>",
    "/roleplay/get-roleplay-interview-result/"
  );
  return globalAxiosInstance.get(
    `roleplay/get-roleplay-interview-result/${roleplayId}`
  );
};

export const interviewUpdate = async (interviewId: string, body: any) => {
  return globalAxiosInstance.patch(
    `/user-interview/interview-update/${interviewId}`,
    body
  );
};

export const interviewScoreUpdate = async (interviewId: string) => {
  return globalAxiosInstance.get(
    `/user-interview/score-of-interview/${interviewId}`
  );
};
export const getOrgSettings = (orgId: string) => {
  return globalAxiosInstance.get(`/organizations/settings/${orgId}`);
};

// export const sendThankyouEmailApi = (body: {
//   assignedInterviewId: string;
//   orgName: string;
// }) => {
//   return globalAxiosInstance.post(`/email/thankyou-email`, body);
// };

export const validateInterviewApi = (interviewId: string) => {
  return globalAxiosInstance.get(`/user-interview/validate/${interviewId}`);
};

export const getInterviewById = (interviewId: string) => {
  return globalAxiosInstance.get(
    `/user-interview/get-interview-email/${interviewId}`
  );
};

export const getInterviewModulesAccessStatusApi = (
  bookId: string,
  jobId: string,
  interviewId: string
) => {
  return globalAxiosInstance.get(
    `/users/interview-modules-access-status/${bookId}/${jobId}/${interviewId}`
  );
};

export interface EmailSubscriptionRequest {
  userId: string;
  action: "unsubscribe" | "subscribe";
  reason?: string;
}

/**
 * Both subscription calls are public (token-less) because they are reached from
 * the email footer link by users who may not be signed in. The userId in the
 * link is what identifies the user on the server.
 */
export const getEmailSubscriptionApi = (userId: string) => {
  return globalAxiosInstance.get<{ data: { email: string } }>(
    `/users/email-subscription/${encodeURIComponent(userId)}`
  );
};

export const updateEmailSubscriptionApi = (body: EmailSubscriptionRequest) => {
  return globalAxiosInstance.post("/users/email-subscription", body);
};
